import { useEffect, useState } from "react";
import api, { apiErr, fmtMoney, fmtDate, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CAN_DISBURSE, PAYMENT_MODES } from "@/lib/constants";
import { PageHeader, Loader, EmptyState } from "@/components/ui-kit";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, MoreVertical, Pencil, Trash2, Wallet, Trash, Download } from "lucide-react";
import { toast } from "sonner";

// Renders a full CRUD + paiements page for a "payable" resource (charges / impôts / cotisations)
export default function PayableModule({ endpoint, title, subtitle, icon: Icon, entityLabel, fields, columns, emptyForm }) {
  const { role } = useAuth();
  const canManage = CAN_DISBURSE.includes(role);
  const [items, setItems] = useState(null);
  const [settings, setSettings] = useState({});
  const [accounts, setAccounts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [payFor, setPayFor] = useState(null);
  const [pay, setPay] = useState({ montant: 0, mode: "Espèces", account_id: "", date: "", note: "" });

  const load = () => api.get(`/${endpoint}`).then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/cash-accounts").then((r) => setAccounts(r.data)).catch(() => {});
    if (fields.some((f) => f.type === "beneficiary")) {
      api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
      api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
    }
  }, [endpoint]);

  const openNew = () => { setForm({ ...emptyForm, date: new Date().toISOString().slice(0, 10) }); setOpen(true); };
  const openEdit = (row) => { setForm({ ...row }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (form.id) await api.put(`/${endpoint}/${form.id}`, form);
      else await api.post(`/${endpoint}`, form);
      toast.success("Enregistré"); setOpen(false); load();
    } catch (err) { toast.error(apiErr(err)); } finally { setSaving(false); }
  };
  const remove = async (id) => { if (!window.confirm("Supprimer cet élément ?")) return; await api.delete(`/${endpoint}/${id}`); toast.success("Supprimé"); load(); };

  const openPay = (row) => { setPayFor(row); setPay({ montant: row.solde || 0, mode: "Espèces", account_id: "", date: new Date().toISOString().slice(0, 10), note: "" }); };
  const addPayment = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post(`/${endpoint}/${payFor.id}/payments`, pay);
      toast.success("Règlement enregistré"); setPayFor(r.data); load();
      setPay({ montant: r.data.solde || 0, mode: "Espèces", account_id: "", date: new Date().toISOString().slice(0, 10), note: "" });
    } catch (err) { toast.error(apiErr(err)); }
  };
  const rmPayment = async (payid) => { const r = await api.delete(`/${endpoint}/${payFor.id}/payments/${payid}`); setPayFor(r.data); load(); };
  const downloadReceipt = () => {
    const token = localStorage.getItem("omnibat_token");
    fetch(`${API_BASE}/${endpoint}/${payFor.id}/receipt`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => { const url = URL.createObjectURL(b); const a = document.createElement("a"); a.href = url; a.download = `recu_${payFor.numero}.pdf`; a.click(); });
  };

  const list = items || [];
  const totalDu = list.filter((x) => x.statut !== "Annulée").reduce((s, x) => s + (x.solde || 0), 0);
  const optionsFor = (f) => (f.optionsKey ? (settings[f.optionsKey] || []) : (f.options || []));

  return (
    <div>
      <PageHeader title={title} subtitle={subtitle}>
        <div className="rounded-lg border bg-white px-4 py-2 mr-2 hidden sm:block">
          <p className="text-[10px] uppercase font-mono text-slate-400">Reste à payer</p>
          <p className="font-mono font-bold text-rose-600">{fmtMoney(totalDu)}</p>
        </div>
        {canManage && <Button onClick={openNew} data-testid={`add-${entityLabel}`} className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1.5" />Nouveau</Button>}
      </PageHeader>

      {items === null ? <Loader /> : list.length === 0 ? (
        <EmptyState icon={Icon} title={`Aucun élément`} subtitle={subtitle} />
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden"><div className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>N°</TableHead>
              {columns.map((c) => <TableHead key={c.label}>{c.label}</TableHead>)}
              <TableHead className="text-right">Montant</TableHead><TableHead className="text-right">Payé</TableHead>
              <TableHead className="text-right">Solde</TableHead><TableHead>Échéance</TableHead><TableHead>État</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {list.map((row) => (
                <TableRow key={row.id} className="hover:bg-slate-50" data-testid={`${entityLabel}-row-${row.id}`}>
                  <TableCell className="font-mono text-xs text-slate-500">{row.numero}</TableCell>
                  {columns.map((c) => <TableCell key={c.label} className={c.className}>{c.render(row)}</TableCell>)}
                  <TableCell className="text-right font-mono text-xs font-bold">{fmtMoney(row.montant)}</TableCell>
                  <TableCell className="text-right font-mono text-xs text-emerald-600">{fmtMoney(row.montant_paye)}</TableCell>
                  <TableCell className={`text-right font-mono text-xs font-semibold ${row.solde > 0 ? "text-rose-600" : "text-slate-400"}`}>{fmtMoney(row.solde)}</TableCell>
                  <TableCell className="text-sm">{row.echeance ? fmtDate(row.echeance) : "—"}</TableCell>
                  <TableCell><StatusBadge value={row.etat} /></TableCell>
                  <TableCell>
                    {canManage && <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`${entityLabel}-menu-${row.id}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openPay(row)} data-testid={`${entityLabel}-pay-${row.id}`}><Wallet className="h-4 w-4 mr-2" />Règlements</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openEdit(row)}><Pencil className="h-4 w-4 mr-2" />Modifier</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => remove(row.id)} className="text-rose-600"><Trash2 className="h-4 w-4 mr-2" />Supprimer</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div></div>
      )}

      {/* Form dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">{form?.id ? "Modifier" : "Nouveau"} — {title}</DialogTitle></DialogHeader>
          {form && (
            <form onSubmit={save} className="grid grid-cols-2 gap-3">
              {fields.map((f) => (
                <div key={f.key} className={f.span === 2 ? "col-span-2" : ""}>
                  <Label className="text-xs">{f.label}{f.required && " *"}</Label>
                  {f.type === "beneficiary" ? (() => {
                    const salary = /salair/i.test(form.categorie || "");
                    const opts = salary
                      ? employees.map((e) => `${e.prenom || ""} ${e.nom || ""}`.trim()).filter(Boolean)
                      : suppliers.map((sp) => sp.nom).filter(Boolean);
                    return (
                      <Select value={form[f.key] || ""} onValueChange={(v) => setForm({ ...form, [f.key]: v })}>
                        <SelectTrigger className="mt-1" data-testid={`field-${f.key}`}>
                          <SelectValue placeholder={salary ? "Sélectionner un employé" : "Sélectionner un fournisseur"} />
                        </SelectTrigger>
                        <SelectContent>
                          {opts.length === 0 && <SelectItem value="__none" disabled>{salary ? "Aucun collaborateur" : "Aucun fournisseur"}</SelectItem>}
                          {opts.map((o, i) => <SelectItem key={`${o}-${i}`} value={o}>{o}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    );
                  })() : f.type === "select" ? (
                    <Select value={form[f.key] || ""} onValueChange={(v) => setForm({ ...form, [f.key]: v })}>
                      <SelectTrigger className="mt-1" data-testid={`field-${f.key}`}><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                      <SelectContent>{optionsFor(f).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : f.type === "textarea" ? (
                    <Textarea value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} rows={2} className="mt-1" data-testid={`field-${f.key}`} />
                  ) : (
                    <Input type={f.type || "text"} value={form[f.key] ?? ""} required={f.required}
                      onChange={(e) => setForm({ ...form, [f.key]: f.type === "number" ? Number(e.target.value) : e.target.value })}
                      className="mt-1" data-testid={`field-${f.key}`} />
                  )}
                </div>
              ))}
              <DialogFooter className="col-span-2"><Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                <Button type="submit" disabled={saving} data-testid={`${entityLabel}-submit`} className="bg-brand-600 hover:bg-brand-700 text-white">Enregistrer</Button></DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Payments dialog */}
      <Dialog open={!!payFor} onOpenChange={(o) => !o && setPayFor(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-head flex items-center justify-between gap-2"><span>Règlements — {payFor?.numero}</span>
            {(payFor?.payments || []).length > 0 && <Button type="button" variant="outline" size="sm" onClick={downloadReceipt} data-testid="download-receipt"><Download className="h-4 w-4 mr-1.5" />Reçu PDF</Button>}
          </DialogTitle></DialogHeader>
          {payFor && (<>
            <div className="grid grid-cols-3 gap-2 text-center mb-2">
              <div className="rounded-lg bg-slate-50 p-2"><p className="text-[10px] uppercase text-slate-400">Montant</p><p className="font-mono text-sm font-bold">{fmtMoney(payFor.montant)}</p></div>
              <div className="rounded-lg bg-emerald-50 p-2"><p className="text-[10px] uppercase text-slate-400">Payé</p><p className="font-mono text-sm font-bold text-emerald-600">{fmtMoney(payFor.montant_paye)}</p></div>
              <div className="rounded-lg bg-rose-50 p-2"><p className="text-[10px] uppercase text-slate-400">Solde</p><p className="font-mono text-sm font-bold text-rose-600">{fmtMoney(payFor.solde)}</p></div>
            </div>
            {(payFor.payments || []).length > 0 && (
              <div className="rounded-lg border divide-y max-h-40 overflow-y-auto">
                {payFor.payments.map((p) => (
                  <div key={p.id} className="flex items-center justify-between px-3 py-2 text-sm">
                    <div><span className="font-mono font-semibold">{fmtMoney(p.montant)}</span><span className="text-slate-400 ml-2">{p.mode}{p.account_name ? ` · ${p.account_name}` : ""} · {fmtDate(p.date)}</span></div>
                    <button onClick={() => rmPayment(p.id)}><Trash className="h-4 w-4 text-slate-300 hover:text-rose-500" /></button>
                  </div>
                ))}
              </div>
            )}
            {payFor.solde > 0 && (
              <form onSubmit={addPayment} className="space-y-3 border-t pt-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Montant</Label><Input type="number" value={pay.montant} onChange={(e) => setPay({ ...pay, montant: Number(e.target.value) })} required className="mt-1" data-testid="pay-montant" /></div>
                  <div><Label className="text-xs">Mode</Label>
                    <Select value={pay.mode} onValueChange={(v) => setPay({ ...pay, mode: v })}><SelectTrigger className="mt-1" data-testid="pay-mode"><SelectValue /></SelectTrigger>
                      <SelectContent>{PAYMENT_MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent></Select></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Débiter une caisse</Label>
                    <Select value={pay.account_id || "none"} onValueChange={(v) => setPay({ ...pay, account_id: v === "none" ? "" : v })}><SelectTrigger className="mt-1" data-testid="pay-account"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="none">Aucune (suivi seul)</SelectItem>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.nom} · {fmtMoney(a.solde)}</SelectItem>)}</SelectContent></Select></div>
                  <div><Label className="text-xs">Date</Label><Input type="date" value={pay.date} onChange={(e) => setPay({ ...pay, date: e.target.value })} className="mt-1" /></div>
                </div>
                <DialogFooter><Button type="submit" className="bg-brand-600 hover:bg-brand-700 text-white" data-testid="pay-submit">Ajouter le règlement</Button></DialogFooter>
              </form>
            )}
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
